/**
 * The single definition of client-endpoint delivery health, shared by the cloud dashboard and the device
 * WebConfig so the same endpoint can't be called "healthy" in one and "degraded" in the other.
 *
 * It used to be split: the cloud computed a verdict server-side (ClientEndpointController.GetStatusForHistory,
 * over persisted delivery history) and WebConfig had no equivalent at all, so it passed no severity and the
 * health line's optional prop defaulted to green. Two hosts, one concept, and only one of them could ever
 * report a problem.
 *
 * The hosts differ in what they can observe, so each supplies what it has and this decides:
 *   - the cloud has persisted history (recent errors / slow responses) → historySeverity
 *   - the device has live delivery state (failure streak, buffered events, dropped windows)
 * Absent inputs are "no information", never "fine".
 */

export type EndpointSeverity = "ok" | "warn" | "error" | "unknown";

export type DeliveryHealthInput = {
    lastSuccessUtc?: string | Date | null;
    /** Sends that have failed back-to-back; > 0 means delivery is broken right now. */
    consecutiveFailures?: number | null;
    /** Windows abandoned because a flush hit its per-send cap — real data loss, surfaced separately. */
    parkedWindows?: number | null;
    /** Events waiting for the next scheduled send. NOT a backlog — see isBehind. */
    bufferedEventCount?: number | null;
    /** Buffer age is what reveals a stalled flush; the count alone can't. */
    oldestBufferedUtc?: string | Date | null;
    /** Seconds between scheduled sends. Without it, buffer age can't be judged. */
    expectedIntervalSeconds?: number | null;
    /** Verdict from persisted delivery history, for hosts that keep it. */
    historySeverity?: EndpointSeverity;
    /** How many recent sends the history verdict was judged over, for phrasing the reason. */
    recentSendsConsidered?: number | null;
    /** Recent sends that failed — a warn cause once the latest send is succeeding again. */
    recentFailureCount?: number | null;
    /** Recent sends slower than the server's slow threshold. */
    recentSlowSendCount?: number | null;
    /** Slowest recent send, in ms, so a slow warning can quantify itself. */
    recentMaxMs?: number | null;
    /** Injectable clock, so the behind-ness rule is testable. */
    now?: Date;
};

export type DeliveryHealth = {
    severity: EndpointSeverity;
    /** Delivery is failing right now (a live failure streak). */
    isFailing: boolean;
    /** The buffer is not draining: its oldest event predates several scheduled sends. */
    isBehind: boolean;
    /** When the undrained buffer starts, for "behind since X". */
    behindSinceUtc?: Date;
    /**
     * Short phrase for WHY the endpoint is amber — "slow — 3 of last 5 sends, up to 35s". Undefined when
     * there's nothing to explain, or when another part of the line already says it (a failure streak and an
     * un-draining buffer both render their own detail).
     */
    reason?: string;
};

/**
 * How many scheduled sends' worth of un-drained buffer counts as behind.
 *
 * A buffer that fills between sends and empties on each one is normal operation — at a 2-minute cadence a
 * healthy endpoint constantly shows tens of events and the count sawtooths as it drains. One interval of
 * age is therefore expected (the currently-open window is deliberately retained until it closes), so only
 * age beyond a second full interval means a send was actually missed.
 */
export const BEHIND_INTERVAL_MULTIPLIER = 2;

const SEVERITY_RANK: Record<EndpointSeverity, number> = { unknown: 0, ok: 1, warn: 2, error: 3 };

/** Worst severity wins, with "unknown" never masking a real signal. */
const worst = (a: EndpointSeverity, b: EndpointSeverity): EndpointSeverity =>
    SEVERITY_RANK[a] >= SEVERITY_RANK[b] ? a : b;

const asDate = (value?: string | Date | null): Date | undefined => {
    if (value === null || value === undefined || value === "") return undefined;
    const d = value instanceof Date ? value : new Date(value);
    return isNaN(d.getTime()) ? undefined : d;
};

export const deliveryHealth = (input: DeliveryHealthInput): DeliveryHealth => {
    const now = input.now ?? new Date();
    const isFailing = (input.consecutiveFailures ?? 0) > 0;

    // Buffer AGE, not size, is the signal. Judging it needs the send interval; without one there is
    // nothing to compare against, so report not-behind rather than guessing.
    const oldestBuffered = asDate(input.oldestBufferedUtc);
    const interval = input.expectedIntervalSeconds ?? null;
    const isBehind =
        !!oldestBuffered &&
        interval !== null &&
        interval > 0 &&
        now.getTime() - oldestBuffered.getTime() > BEHIND_INTERVAL_MULTIPLIER * interval * 1000;

    let severity: EndpointSeverity;
    if (isFailing) {
        severity = "error";
    } else {
        // Nothing has ever been delivered and nothing is wrong yet — genuinely unknown, not healthy.
        const everDelivered = !!asDate(input.lastSuccessUtc);
        const base: EndpointSeverity = isBehind ? "warn" : everDelivered ? "ok" : "unknown";
        severity = worst(base, input.historySeverity ?? "unknown");
    }

    return {
        severity,
        isFailing,
        isBehind,
        behindSinceUtc: isBehind ? oldestBuffered : undefined,
        reason: severity === "warn" ? warnReason(input, isBehind) : undefined,
    };
};

/** "35s", or "854ms" below a second — a bare millisecond count is hard to read at these magnitudes. */
const humanizeMs = (ms: number): string => (ms >= 1000 ? `${Math.round(ms / 1000)}s` : `${Math.round(ms)}ms`);

// Only for the amber case. Failing and behind are excluded because the line already renders their own
// detail ("failing (3x since ...)", "behind — 27 event(s) queued since ..."), and repeating it reads badly.
const warnReason = (input: DeliveryHealthInput, isBehind: boolean): string | undefined => {
    if (isBehind) return undefined;

    const window = input.recentSendsConsidered ?? 0;
    const slow = input.recentSlowSendCount ?? 0;
    const failed = input.recentFailureCount ?? 0;
    const ofWindow = window > 0 ? ` of last ${window} sends` : "";

    if (slow > 0) {
        const peak = input.recentMaxMs ? `, up to ${humanizeMs(input.recentMaxMs)}` : "";
        return `slow — ${slow}${ofWindow}${peak}`;
    }
    if (failed > 0) return `recovering — ${failed} recent failure${failed === 1 ? "" : "s"}${ofWindow}`;
    return undefined;
};

/**
 * How reliably an endpoint has been delivering over a window, as text. The rendering of the window
 * rollup lives here rather than in EndpointHealthStrip because more than one host needs the same
 * sentence: the dashboard prints it under the endpoint row, the Fleet Commander console prints it in
 * a truck's Health card, and "98.2% of 340 sends" has to mean the same thing in both. The strip's
 * hourly bars stay with the strip — they are a dashboard-sized graphic, not a phrase.
 *
 * Typed structurally so this module stays import-free: callers hand in the health window they hold.
 */
export type DeliveryWindowFacts = {
    sends: number;
    failures: number;
    windowStartUtc?: string | Date | null;
    windowEndUtc?: string | Date | null;
};

/**
 * Below this many sends a percentage is more precise than the evidence supports — at 3 sends each one
 * is worth 33 points — so the raw fraction is shown instead.
 */
export const MIN_SENDS_FOR_RATE = 20;

/**
 * The window's headline reliability figure. Returns undefined when there is nothing to report — an
 * endpoint with no sends has no success rate, and rendering it as 0% would read as total failure.
 */
export const formatRate = (window: DeliveryWindowFacts): string | undefined => {
    if (window.sends === 0) return undefined;
    const succeeded = window.sends - window.failures;
    if (window.sends < MIN_SENDS_FOR_RATE) return `${succeeded} of ${window.sends} sends`;
    // One decimal: 99.2% and 99.8% are operationally different, 99.23% is noise.
    return `${((succeeded / window.sends) * 100).toFixed(1)}% of ${window.sends.toLocaleString()} sends`;
};

/** How many days the window spans, for the "· 30d" suffix. Hours below a day. */
export const formatWindowSpan = (window: DeliveryWindowFacts): string | undefined => {
    const start = asDate(window.windowStartUtc)?.getTime();
    const end = asDate(window.windowEndUtc)?.getTime();
    if (start === undefined || end === undefined || !(end > start)) return undefined;
    const days = Math.round((end - start) / 86_400_000);
    return days >= 1 ? `${days}d` : `${Math.round((end - start) / 3_600_000)}h`;
};
